<template>
    <div data-testid="step-taxResidence" class="container">
        <h1 class="container__standard-header">
            {{ $t(`${TRANSLATION_KEY}.taxResidenceHeader`) }}
        </h1>

        <form
            class="form-for-tax-residence"
            @submit.prevent="null"
            @keydown.tab="handlePendingTabNavigation"
        >
            <TaxResidenceField
                :name="TaxField.FIRST_TAX_RESIDENCE"
                :pass-default-tax-country="
                    null !== taxResidenceData
                        ? taxResidenceData.firstTaxCountry
                        : ''
                "
                :country-disable="
                    taxResidenceData?.firstTaxCountry ? true : false
                "
                :pass-default-tin-number="firstTinNumberFromServer"
                cy-data="tax-residence-first"
                @input="receiveData(TaxField.FIRST_TAX_RESIDENCE, $event)"
                @validate-tin="handleValidateTin"
            />

            <div>
                <div
                    v-if="isOpenSecondTaxResidence"
                    class="row--next-tax-residence"
                >
                    <div class="row--next-tax-residence__describe-close-form">
                        <span class="tax-residence-text">
                            {{ $t(`${TRANSLATION_KEY}.anotherTaxResidence`) }}
                        </span>
                        <button
                            type="button"
                            class="tax-residence-close-form"
                            @click="
                                handleTaxResidence(
                                    TaxField.SECOND_TAX_RESIDENCE
                                )
                            "
                        />
                    </div>
                    <TaxResidenceField
                        :name="TaxField.SECOND_TAX_RESIDENCE"
                        cy-data="tax-residence-two"
                        :pass-default-tax-country="
                            taxResidenceData?.secondTaxCountry || ''
                        "
                        @input="
                            receiveData(TaxField.SECOND_TAX_RESIDENCE, $event)
                        "
                        @validate-tin="handleValidateTin"
                    />
                </div>

                <div
                    v-if="isOpenThirdTaxResidence"
                    class="row--next-tax-residence"
                >
                    <div class="row--next-tax-residence__describe-close-form">
                        <span class="tax-residence-text">
                            {{ $t(`${TRANSLATION_KEY}.anotherTaxResidence`) }}
                        </span>
                        <button
                            type="button"
                            class="tax-residence-close-form"
                            @click="
                                handleTaxResidence(TaxField.THIRD_TAX_RESIDENCE)
                            "
                        />
                    </div>
                    <TaxResidenceField
                        :name="TaxField.THIRD_TAX_RESIDENCE"
                        cy-data="tax-residence-three"
                        :pass-default-tax-country="
                            taxResidenceData?.thirdTaxCountry || ''
                        "
                        @input="
                            receiveData(TaxField.THIRD_TAX_RESIDENCE, $event)
                        "
                        @validate-tin="handleValidateTin"
                    />
                </div>

                <div
                    v-if="!isOpenThirdTaxResidence"
                    class="row--add-new-tax-residence"
                >
                    <button
                        type="button"
                        v-if="!isOpenSecondTaxResidence"
                        class="add-tax-residence"
                        cy-data="add-tax-residence"
                        @click="
                            handleTaxResidence(TaxField.SECOND_TAX_RESIDENCE)
                        "
                    >
                        <span class="add-tax-residence__text">
                            {{ $t(`${TRANSLATION_KEY}.addNewTaxResidence`) }}
                        </span>
                        <span class="add-tax-residence__char" />
                    </button>
                    <button
                        type="button"
                        v-else
                        class="add-tax-residence"
                        cy-data="add-tax-residence"
                        @click="
                            handleTaxResidence(TaxField.THIRD_TAX_RESIDENCE)
                        "
                    >
                        <span class="add-tax-residence__text">
                            {{ $t(`${TRANSLATION_KEY}.addNewTaxResidence`) }}
                        </span>
                        <span class="add-tax-residence__char" />
                    </button>
                    <Error
                        name="addSecondTaxCountry"
                        class="error-tax-residence-position"
                    />
                </div>
            </div>

            <div class="field--poland-tax-residence-field__disclaimer">
                {{ $t(`${TRANSLATION_KEY}.disclaimer`) }}
            </div>

            <SubmitButton
                :action="Action.TAX_OFFICE_DETAILS"
                data-testid="btn-taxResidence-nextStep"
                :button-value="$t(`${TRANSLATION_KEY}.next`)"
                :data="formData"
                class="button button--submit"
                :disabled="!formIsValid"
                @click="handleNextStep"
            />
        </form>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    computed,
    onMounted,
    onUnmounted,
    nextTick,
} from 'vue';
import { useStore } from 'vuex';
import TaxResidenceField from '@/components/compositeFields/TaxResidenceField.vue';
import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import Error from '@/components/basic/Error.vue';
import { TaxResidenceFlags } from '@/constants/tax-residence-flags';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
} from '@/utils/AmplitudeAnalyticsEvents';
import { PROGRESS_BAR_TIMES } from '@/constants/progress-bar-times';
import { Action } from '@/constants/ajax';

interface TinValidatePayload {
    name: string;
    tinNumber: string;
    taxCountry: string;
}

const defaultTaxOfficeForeigner = {
    TAX_OFFICE_ID: 1076,
    TAX_OFFICE_NAME:
        'Trzeci Urząd Skarbowy Warszawa-Śródmieście - ul. Lindleya 14, 02-013',
};

const TaxField = {
    FIRST_TAX_RESIDENCE: 'first',
    SECOND_TAX_RESIDENCE: 'second',
    THIRD_TAX_RESIDENCE: 'third',
};

export default defineComponent({
    name: 'TaxResidence',
    components: {
        TaxResidenceField,
        SubmitButton,
        Error,
    },
    setup() {
        const store = useStore();
        const TaxFieldRef = TaxField;

        const FIELDS_SCHEMA = [
            'TaxCountry',
            'TinNumber',
            'NoTinNumber',
            'NoTinNumberReason',
            'NoTinNumberOtherReason',
            'PolandTaxOfficeId',
            'PolandTaxOfficeName',
            'PolandTaxResidentPesel',
            'EmptyTinNumber',
        ];

        const generateInitialFormData = () => {
            const state: Record<string, any> = {};

            Object.values(TaxField).forEach((prefix) => {
                FIELDS_SCHEMA.forEach((field) => {
                    state[`${prefix}${field}`] = null;
                });

                if (prefix !== TaxField.FIRST_TAX_RESIDENCE) {
                    const capitalizedPrefix =
                        prefix.charAt(0).toUpperCase() + prefix.slice(1);
                    state[`add${capitalizedPrefix}TaxCountry`] = false;
                }
            });

            return state;
        };

        const formData = ref(generateInitialFormData());

        const taxOfficeDataForeigner = ref({});
        const isOpenSecondTaxResidence = ref(false);
        const isOpenThirdTaxResidence = ref(false);
        const pendingTabTarget = ref<string | null>(null);
        const TRANSLATION_KEY = 'steps.TaxResidence';
        const formIsValid = ref(false);
        let validationIntervalId: number | null = null;

        const taxResidenceData = computed(() => store.state.formData || {});

        const firstTinNumberFromServer = computed<string>(() => {
            const data = taxResidenceData.value;
            return typeof data.suggestedFirstTinNumber === 'string'
                ? data.suggestedFirstTinNumber.trim()
                : '';
        });

        const handleValidateTin = (payload: TinValidatePayload) => {
            const prefix = payload.name; // "first" / "second" / "third"

            if (!payload.tinNumber) {
                return;
            }

            store.dispatch('validate', {
                [`${prefix}TinNumber`]: payload.tinNumber,
                [`${prefix}TaxCountry`]: payload.taxCountry,
            });
        };

        const setDefaultTaxOfficeForeigner = () => {
            taxOfficeDataForeigner.value = {
                key: defaultTaxOfficeForeigner.TAX_OFFICE_ID,
                value: defaultTaxOfficeForeigner.TAX_OFFICE_NAME,
            };
        };

        const setFieldIfForeignerInPoland = () => {
            if (
                taxResidenceData.value.taxResidenceFlag ===
                TaxResidenceFlags.FOREIGNER_LIVE_POLAND
            ) {
                setDefaultTaxOfficeForeigner();
            }
        };

        const isValid = () => {
            const customFields = [
                ...document.querySelectorAll('label input'),
                ...document.querySelectorAll(
                    '.custom-select-wrapper .custom-select'
                ),
            ].filter((el) => {
                if (el instanceof HTMLInputElement) {
                    const isSearchField =
                        el.type === 'search' || el.name === 'tax-office';

                    return !isSearchField;
                }
                return true;
            });
            const fieldsLength = customFields.length;
            const validFields = [
                ...document.querySelectorAll('label.valid'),
                ...document.querySelectorAll('div.valid'),
            ];
            const form = formData.value as Record<string, any>;
            let extraValidationOk = true;
            Object.keys(form)
                .filter((key) => key.endsWith('NoTinNumber'))
                .forEach((noTinKey) => {
                    const reasonKey = noTinKey.replace(
                        'NoTinNumber',
                        'NoTinNumberReason'
                    );
                    const otherReasonKey = noTinKey.replace(
                        'NoTinNumber',
                        'NoTinNumberOtherReason'
                    );
                    const noTinValue = form[noTinKey];
                    const reasonValue = form[reasonKey];
                    const otherReasonValue = form[otherReasonKey];
                    if (noTinValue && !reasonValue && !otherReasonValue) {
                        extraValidationOk = false;
                    }
                });
            formIsValid.value =
                validFields.length >= fieldsLength && extraValidationOk;
        };

        const receiveData = (name: string, taxOfficeData: any) => {
            for (const entry in taxOfficeData) {
                if (
                    Object.prototype.hasOwnProperty.call(taxOfficeData, entry)
                ) {
                    const newName =
                        entry.charAt(0).toUpperCase() + entry.slice(1);
                    const key = name + newName;

                    if (key in formData.value) {
                        formData.value[key] = taxOfficeData[entry];
                    }
                }
            }

            const addKey =
                'add' +
                name.charAt(0).toUpperCase() +
                name.slice(1) +
                'TaxCountry';
            if (addKey in formData.value) formData.value[addKey] = true;

            isValid();
        };

        const getTaxResidenceCountryField = (taxResidence: string) => {
            return document.getElementById(
                `${taxResidence}TaxCountry`
            ) as HTMLElement | null;
        };

        const focusPendingTaxResidenceField = async () => {
            if (!pendingTabTarget.value) {
                return;
            }

            await nextTick();

            const input = getTaxResidenceCountryField(pendingTabTarget.value);

            if (input) {
                input.focus();
                pendingTabTarget.value = null;
            }
        };

        const handlePendingTabNavigation = (evt: KeyboardEvent) => {
            if (evt.key !== 'Tab' || evt.shiftKey || !pendingTabTarget.value) {
                return;
            }

            evt.preventDefault();
            focusPendingTaxResidenceField();
        };

        const startValidationPolling = () => {
            validationIntervalId = window.setInterval(isValid, 100);
        };

        const stopValidationPolling = () => {
            if (validationIntervalId === null) {
                return;
            }

            window.clearInterval(validationIntervalId);
            validationIntervalId = null;
        };

        const handleTaxResidence = (taxResidence: any) => {
            let isOpening = false;

            if (taxResidence === TaxField.SECOND_TAX_RESIDENCE) {
                if (!isOpenThirdTaxResidence.value) {
                    isOpening = !isOpenSecondTaxResidence.value;
                    isOpenSecondTaxResidence.value =
                        !isOpenSecondTaxResidence.value;
                }
            } else {
                isOpening = !isOpenThirdTaxResidence.value;
                isOpenThirdTaxResidence.value = !isOpenThirdTaxResidence.value;
            }
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.taxResidence
                    .TAX_RESIDENCY_MORE_THAN_ONE
            );

            pendingTabTarget.value = isOpening ? taxResidence : null;

            if (
                (taxResidence === TaxField.SECOND_TAX_RESIDENCE &&
                    !isOpenSecondTaxResidence.value) ||
                (taxResidence === TaxField.THIRD_TAX_RESIDENCE &&
                    !isOpenThirdTaxResidence.value)
            ) {
                resetData(taxResidence);
            }
        };

        const handleNextStep = () => {
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.taxResidence.TAX_RESIDENCY_CONTINUE
            );
        };

        const resetData = (prefix: string) => {
            FIELDS_SCHEMA.forEach((field) => {
                formData.value[`${prefix}${field}`] = null;
            });

            if (prefix !== TaxField.FIRST_TAX_RESIDENCE) {
                const capitalizedPrefix =
                    prefix.charAt(0).toUpperCase() + prefix.slice(1);
                const addKey = `add${capitalizedPrefix}TaxCountry`;
                if (formData.value[addKey] !== undefined) {
                    formData.value[addKey] = false;
                }
            }
        };

        onMounted(() => {
            store.commit('SET_PROGRESS', PROGRESS_BAR_TIMES.TAX_RESIDENCE);

            if (Object.keys(taxResidenceData.value).length > 0) {
                Object.assign(formData.value, taxResidenceData.value);
            }

            if (
                formData.value.secondTaxCountry ||
                formData.value.addSecondTaxCountry
            ) {
                isOpenSecondTaxResidence.value = true;
            }
            if (
                formData.value.thirdTaxCountry ||
                formData.value.addThirdTaxCountry
            ) {
                isOpenThirdTaxResidence.value = true;
            }

            setFieldIfForeignerInPoland();
            isValid();
            startValidationPolling();
        });

        onUnmounted(() => {
            stopValidationPolling();
        });

        return {
            TaxField: TaxFieldRef,
            formData,
            taxOfficeDataForeigner,
            isOpenSecondTaxResidence,
            isOpenThirdTaxResidence,
            TRANSLATION_KEY,
            taxResidenceData,
            firstTinNumberFromServer,
            formIsValid,
            Action,
            receiveData,
            handleTaxResidence,
            resetData,
            isValid,
            handlePendingTabNavigation,
            amplitudeSendEvent,
            handleNextStep,
            handleValidateTin,
        };
    },
});
</script>
